<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuth } from '@/composables/useAuth';
import LoginForm from '@/components/auth/LoginForm.vue';
import { hasAdminRole, type LoginParams } from '@/types/auth';
import { useBrandingStore } from '@/stores/branding';
import { showWarning } from '@/utils/message';
import { forceChangePasswordPath } from '@/utils/passwordEntry';

const router = useRouter();
const route = useRoute();
const { state, login } = useAuth();
const loading = ref(false);
const errorMsg = ref('');
const loginFormRef = ref<InstanceType<typeof LoginForm> | null>(null);

// 系统名称/Logo 来自管理端"基础设置"（公开接口，失败回退默认品牌名）
const brandingStore = useBrandingStore();
const systemName = computed(() => brandingStore.systemName || '智台账');
const brandLogo = computed(() => brandingStore.logo);

const isAuthenticated = computed(() => state.isAuthenticated);
const isAdmin = computed(() => hasAdminRole(state.currentUser?.role));

function redirectToWorkspace() {
  const target = isAdmin.value ? '/admin' : '/dashboard';
  router.push(target);
}

// 已登录用户访问登录页时自动跳转；同时加载品牌信息
onMounted(() => {
  brandingStore.loadBranding();
  if (isAuthenticated.value) {
    redirectToWorkspace();
  }
});

// 监听登录态变化（防止登录成功后 router-push 未及时生效时仍显示登录表单）
watch(isAuthenticated, (loggedIn) => {
  if (loggedIn) {
    redirectToWorkspace();
  }
});

// 修复登录审查#15：登录后优先回跳被拦截页。仅收站内相对路径
// （以单 / 开头、排除 // 协议相对与 backslash 绕过），防开放重定向
function safeRedirectFromQuery(): string | null {
  const raw = route.query.redirect;
  if (typeof raw !== 'string') return null;
  if (!raw.startsWith('/') || raw.startsWith('//') || raw.includes('\\')) return null;
  if (raw === '/login') return null;
  return raw;
}

async function handleSubmit(params: LoginParams) {
  errorMsg.value = '';
  loading.value = true;
  try {
    const result = await login(params);
    if (result.success) {
      // 修复登录审查#11：强制改密标记（后端登录响应 requirePasswordChange）闭环——
      // 引导到改密页，改密前不做常规工作台跳转。按角色分流（口径单源在 utils/passwordEntry）：
      // 管理员留在管理后台的「系统配置 → 账号安全」，不再被赶到用户端改自己的密码
      if (result.user?.forcePasswordChange) {
        showWarning('为了账号安全，请先修改初始密码');
        router.push(forceChangePasswordPath(result.user?.role));
        return;
      }
      const redirect = safeRedirectFromQuery();
      const target = result.requireSetup ? '/setup' : (redirect ?? result.redirectTo ?? '/dashboard');
      router.push(target);
    } else {
      errorMsg.value = result.error || '登录失败';
      if (params.method === 'password') {
        loginFormRef.value?.recordAdminFailure();
      }
    }
  } finally {
    loading.value = false;
  }
}

function handleError(message: string) {
  errorMsg.value = message;
}
</script>

<template>
  <div class="login-page relative flex min-h-screen w-full overflow-hidden">
    <!-- 全屏品牌背景层（柔和，opacity 0.85） -->
    <div class="login-bg absolute inset-0 z-0" aria-hidden="true">
      <div class="absolute inset-0" style="background: var(--gradient-hero);"></div>

      <!-- 网格底纹 -->
      <div
        class="absolute inset-0 opacity-[0.04]"
        style="background-image: linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px); background-size: 60px 60px;"
      ></div>

      <!-- 多层模糊光斑 -->
      <div class="absolute top-16 left-16 h-72 w-72 rounded-full bg-white/15 blur-3xl animate-fade-in"></div>
      <div class="absolute bottom-20 right-24 h-96 w-96 rounded-full bg-purple-300/20 blur-3xl animate-fade-in" style="animation-delay: 0.2s;"></div>
      <div class="absolute top-1/2 left-1/3 h-64 w-64 rounded-full bg-blue-300/15 blur-3xl animate-fade-in" style="animation-delay: 0.4s;"></div>
      <div class="absolute bottom-12 left-1/2 h-56 w-56 rounded-full bg-indigo-300/15 blur-3xl animate-fade-in" style="animation-delay: 0.6s;"></div>

      <!-- 浮动几何图形 -->
      <div class="hidden lg:block absolute top-[18%] right-[28%] h-16 w-16 rounded-full border-2 border-white/20 animate-float-y" style="animation-delay: 0s;"></div>
      <div class="hidden lg:block absolute bottom-[22%] right-[36%] h-10 w-10 rounded-md border-2 border-white/15 rotate-45 animate-float-y" style="animation-delay: 1.2s; animation-duration: 8s;"></div>
      <div class="hidden lg:block absolute top-[60%] left-[12%] h-20 w-20 rounded-full border-2 border-white/10 animate-float-x" style="animation-delay: 0.6s; animation-duration: 9s;"></div>

      <!-- 背景品牌文案（淡） -->
      <div class="relative z-10 hidden lg:flex flex-col justify-center h-full pl-16 xl:pl-24 max-w-[55%]">
        <div class="animate-fade-in-up">
          <div class="mb-8 flex items-center gap-3">
            <img v-if="brandLogo" :src="brandLogo" :alt="systemName" class="h-12 w-12 rounded-xl object-cover" />
            <div v-else class="flex h-12 w-12 items-center justify-center rounded-xl bg-white/15 backdrop-blur-sm">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
                <polyline points="10 9 9 9 8 9"/>
              </svg>
            </div>
            <span class="text-2xl font-bold text-white tracking-tight">{{ systemName }}</span>
          </div>
          <h1 class="text-4xl xl:text-5xl font-bold text-white leading-tight mb-4">
            智能台账<br/>管理平台
          </h1>
          <p class="text-base xl:text-lg text-white/65 leading-relaxed max-w-md">
            一站式台账创建、管理与智能分析平台，助力基层治理数字化转型
          </p>
        </div>

        <div class="mt-12 space-y-4 animate-fade-in-up" style="animation-delay: 0.2s;">
          <div class="flex items-center gap-3 text-white/70">
            <div class="flex h-8 w-8 items-center justify-center rounded-lg bg-white/10">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>
            </div>
            <span class="text-sm">AI 智能填充，一键生成台账</span>
          </div>
          <div class="flex items-center gap-3 text-white/70">
            <div class="flex h-8 w-8 items-center justify-center rounded-lg bg-white/10">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>
            </div>
            <span class="text-sm">多维度数据统计与可视化分析</span>
          </div>
          <div class="flex items-center gap-3 text-white/70">
            <div class="flex h-8 w-8 items-center justify-center rounded-lg bg-white/10">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>
            </div>
            <span class="text-sm">迎检准备、文档中心、知识库一体化</span>
          </div>
        </div>
      </div>

      <!-- 整体降透明度蒙层 -->
      <div class="absolute inset-0 bg-black/10"></div>
    </div>

    <!-- 浮层登录卡片（桌面端右偏大居中，移动端居中） -->
    <div class="login-stage relative z-10 flex w-full min-h-screen items-center justify-center px-4 py-10 lg:justify-end lg:pr-[6%] xl:pr-[10%]">
      <div class="login-card glass-card w-full max-w-[440px] rounded-2xl p-8 sm:p-10 animate-card-in">
        <!-- 移动端 Logo -->
        <div class="mb-6 flex items-center justify-center gap-2.5 lg:hidden">
          <img v-if="brandLogo" :src="brandLogo" :alt="systemName" class="h-10 w-10 rounded-xl object-cover" />
          <div v-else class="flex h-10 w-10 items-center justify-center rounded-xl" style="background: var(--gradient-primary);">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
            </svg>
          </div>
          <span class="text-xl font-bold" style="color: var(--color-primary);">{{ systemName }}</span>
        </div>

        <div class="mb-8 text-center">
          <h1 class="text-2xl font-bold tracking-tight" style="color: var(--color-title);">欢迎登录</h1>
          <p class="mt-2 text-sm" style="color: var(--color-caption);">请选择登录方式继续</p>
        </div>

        <LoginForm
          ref="loginFormRef"
          @submit="handleSubmit"
          @error="handleError"
        />

        <div
          v-if="errorMsg"
          class="error-bar mt-4 pl-4 pr-4 py-3 text-sm rounded-lg animate-error-in"
          style="color: var(--color-error);"
          role="alert"
        >
          {{ errorMsg }}
        </div>
      </div>
    </div>

    <!-- 底部链接（全屏固定底部） -->
    <div class="absolute bottom-6 left-0 right-0 z-10 flex items-center justify-center gap-4 text-xs pointer-events-none" style="color: rgba(255,255,255,0.7);">
      <a href="/service-agreement" class="pointer-events-auto transition hover:text-white">服务协议</a>
      <span>|</span>
      <a href="/privacy-policy" class="pointer-events-auto transition hover:text-white">隐私政策</a>
    </div>
  </div>
</template>

<style scoped>
.login-bg {
  opacity: 0.85;
}

@media (prefers-reduced-motion: reduce) {
  .login-bg .animate-float-y,
  .login-bg .animate-float-x,
  .login-bg .animate-fade-in {
    animation: none !important;
  }
}
</style>
